import React from 'react';
import { Phone, Mail, MapPin, Clock } from 'lucide-react';
import SEO from '../components/SEO';

export default function Contact() {
  const canonical = 'https://divaisetech.com/contact';

  const contactSchema = {
    '@context': 'https://schema.org',
    '@type': 'ContactPage',
    name: 'Contact Divaise Technology',
    url: canonical,
    mainEntity: {
      '@type': 'Organization',
      name: 'Divaise Technology Private Limited',
      url: 'https://divaisetech.com',
      contactPoint: {
        '@type': 'ContactPoint',
        telephone: '+91-8287934973',
        email: 'consult@divaisetech.com',
        contactType: 'customer support',
        areaServed: 'Worldwide',
        availableLanguage: ['en'],
      },
    },
  };

  return (
    <div>
      <SEO
        title="Contact Divaise Technology - Get Your Free Consultation"
        description="Get in touch with Divaise Technology for AI-based IT solutions, automation, hardware fulfillment, and AI chip requirements."
        keywords="contact, consultation, IT services inquiry, Divaise Technology"
        canonical={canonical}
        schema={contactSchema}
      />

      {/* Hero Section */}
      <section className="hero-with-image hero-contact text-white py-16 px-6">
        <div className="max-w-6xl mx-auto">
          <h1 className="text-5xl font-bold mb-6">Get in Touch</h1>
          <p className="text-xl opacity-90 max-w-3xl">
            Have questions about our services? Let's schedule a free 30-minute consultation with our experts.
          </p>
        </div>
      </section>

      {/* Contact Section */}
      <section className="bg-black py-24 px-6">
        <div className="max-w-6xl mx-auto">
          <div className="grid md:grid-cols-3 gap-8 mb-16">
            {/* Contact Info Cards */}
            <div className="bg-white border border-border rounded-xl p-8 shadow-sm hover:shadow-lg transition-all">
              <div className="w-12 h-12 bg-accent/20 rounded-lg flex items-center justify-center mb-4">
                <Phone className="w-6 h-6 text-accent" />
              </div>
              <h3 className="text-xl font-bold mb-2 text-black">Phone</h3>
              <p className="text-gray-700 mb-4">Call us for immediate assistance</p>
              <a href="tel:+918287934973" className="text-accent font-semibold hover:text-accent/90">
                8287934973
              </a>
            </div>

            <div className="bg-white border border-border rounded-xl p-8 shadow-sm hover:shadow-lg transition-all">
              <div className="w-12 h-12 bg-accent/20 rounded-lg flex items-center justify-center mb-4">
                <Mail className="w-6 h-6 text-accent" />
              </div>
              <h3 className="text-xl font-bold mb-2 text-black">Email</h3>
              <p className="text-gray-700 mb-4">Get back to you within 24 hours</p>
              <a href="mailto:consult@divaisetech.com" className="text-accent font-semibold hover:text-accent/90">
                consult@divaisetech.com
              </a>
            </div>

            <div className="bg-white border border-border rounded-xl p-8 shadow-sm hover:shadow-lg transition-all">
              <div className="w-12 h-12 bg-accent/20 rounded-lg flex items-center justify-center mb-4">
                <MapPin className="w-6 h-6 text-accent" />
              </div>
              <h3 className="text-xl font-bold mb-2 text-black">Office</h3>
              <p className="text-gray-800">
                13,14 Floor 4,<br />
                SS Omnia Business Tower,<br />
                Sector 85, Gurgaon
              </p>
            </div>
          </div>

          {/* No-form Contact Section */}
          <div className="grid md:grid-cols-3 gap-12">
            <div className="md:col-span-2">
              <div className="bg-gray-900 border border-gray-700 rounded-xl p-8 shadow-sm">
                <h2 className="text-3xl font-bold mb-4 text-white">Talk to Our Team</h2>
                <p className="text-white/75 mb-8">
                  Choose your preferred channel and connect with our consultants for project planning,
                  service queries, or solution discussions.
                </p>

                <div className="grid md:grid-cols-2 gap-5 mb-8">
                  <a
                    href="mailto:consult@divaisetech.com?subject=Consultation%20Request%20-%20Divaise%20Technology"
                    className="bg-white border border-border rounded-xl p-5 hover:shadow-lg transition-all"
                  >
                    <h3 className="text-lg font-bold text-black mb-1">Send an Email</h3>
                    <p className="text-black/75 text-sm">Best for detailed requirements and document sharing.</p>
                  </a>
                  <a
                    href="https://wa.me/918287934973?text=Hi%20Divaise%20Technology%2C%20I%20need%20help%20with%20a%20project."
                    className="bg-white border border-border rounded-xl p-5 hover:shadow-lg transition-all"
                  >
                    <h3 className="text-lg font-bold text-black mb-1">WhatsApp Chat</h3>
                    <p className="text-black/75 text-sm">Best for quick discussions and faster first response.</p>
                  </a>
                  <a
                    href="tel:+918287934973"
                    className="bg-white border border-border rounded-xl p-5 hover:shadow-lg transition-all"
                  >
                    <h3 className="text-lg font-bold text-black mb-1">Call Directly</h3>
                    <p className="text-black/75 text-sm">Best for urgent conversations and immediate clarifications.</p>
                  </a>
                  <div className="bg-white border border-border rounded-xl p-5">
                    <h3 className="text-lg font-bold text-black mb-1">Schedule a Call</h3>
                    <p className="text-black/75 text-sm">Share preferred time slots by email/WhatsApp and we will confirm.</p>
                  </div>
                </div>

                <div className="bg-gray-800 border border-gray-700 rounded-xl p-6">
                  <h3 className="text-white font-semibold mb-3">Recommended Email Template</h3>
                  <pre className="text-sm text-white/80 whitespace-pre-wrap leading-relaxed">
Subject: Consultation Request - [Company Name]

Name:
Company:
Service Needed: (AI/Automation/Cloud/Hardware/Digital Experience)
Project Summary:
Expected Timeline:
                  </pre>
                </div>
              </div>
            </div>

            {/* Sidebar Info */}
            <div className="space-y-8">
              {/* Business Hours */}
              <div className="bg-gray-900 border border-gray-700 rounded-xl p-6">
                <div className="flex items-center gap-3 mb-4">
                  <Clock className="w-5 h-5 text-accent" />
                  <h3 className="font-bold text-lg text-white">Business Hours</h3>
                </div>
                <div className="space-y-2 text-sm text-white/70">
                  <p>
                    <strong className="text-white">Monday - Friday:</strong>
                    <br />
                    9:00 AM - 6:00 PM IST
                  </p>
                  <p>
                    <strong className="text-white">Saturday:</strong>
                    <br />
                    10:00 AM - 2:00 PM IST
                  </p>
                  <p className="text-white/60">Sunday: Closed</p>
                </div>
              </div>

              {/* Quick Response */}
              <div className="bg-gray-900 border border-gray-700 rounded-xl p-6">
                <p className="font-semibold mb-2 text-white">⚡ Quick Response</p>
                <p className="text-sm text-white/70">
                  We typically respond to inquiries within 24 hours. For urgent matters, please call us directly.
                </p>
              </div>

              {/* Support Channel */}
              <div className="bg-gray-900 border border-gray-700 rounded-xl p-6">
                <p className="font-semibold mb-2 text-white">💬 WhatsApp Support</p>
                <p className="text-sm text-white/70 mb-3">
                  For quick questions, you can also reach us via WhatsApp.
                </p>
                <a
                  href="https://wa.me/918287934973"
                  className="inline-block bg-green-600 text-white px-4 py-2 rounded-lg text-sm font-semibold hover:bg-green-700 transition-all"
                >
                  Chat on WhatsApp
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ CTA */}
      <section className="bg-gray-900 py-16 px-6 border-t border-gray-700">
        <div className="max-w-6xl mx-auto text-center">
          <h2 className="text-3xl font-bold mb-4 text-white">Have Questions?</h2>
          <p className="text-white/70 mb-8">
            Check our FAQ section or scroll down to view common questions about our services.
          </p>
        </div>
      </section>
    </div>
  );
}
